import { MIME_TYPES_AND_FILE_EXTENSION } from "../../config/constants";
import languageModel from "../../models/language.model";
import ByteUtils from "../../utils/byte.utils";

/**
 * File Upload Input Component
 * 
 * General input component used for file upload.
 * Handles basic validation of file extensions.
 * 
 * VULNERABILITY NOTE: Does not validate File Signatures or Double File Extensions
 * 
 * @author Darrus Goh
 */
export default function FileUploadInputComponent() {
    const DEFAULT_ONINPUT = (files) => { console.log('FileUploadInputComponent: onInput(files | file)') };

    let invalidText = '';
    let uploaded = false;

    const handleOnInput = (accept, filesizeLimit, onInput, multiple, required) => (e) => {
        invalidText = validateFiles(e.target.files, accept, filesizeLimit, required);

        if (invalidText !== '') {
            e.target.value = null;
            uploaded = false;
            onInput(null);
        } else {
            uploaded = true;
            onInput(multiple ? e.target.files : e.target.files[0]);
        }
    };

    const validateFiles = (files, acceptedFileTypes, filesizeLimit, required) => {
        if (required && files.length == 0) {
            return languageModel.getText('LT00000729', 'This field is required')
        }

        let consolidatedFilesize = 0;

        for (const file of files) {
            consolidatedFilesize += file.size;

            let validationText = validateExtension(file, acceptedFileTypes);
            if (validationText !== '') {
                return validationText;
            }
        }

        return validateFilesize(consolidatedFilesize, filesizeLimit);
    }

    const validateExtension = (file, acceptedFileTypes) => {
        if (acceptedFileTypes !== '') {
            for (const acceptedFileType of acceptedFileTypes) {
                const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
                const acceptedMimeType = MIME_TYPES_AND_FILE_EXTENSION.find(i => i.extension === extension)?.mimeType;
                if (acceptedFileType.toLowerCase().trim() === extension && acceptedMimeType === file.type) {
                    return '';
                }
            }
            return languageModel.getText('EM00000058', 'Invalid file.');
        }
        return '';
    }

    const validateFilesize = (consolidatedFilesize, filesizeLimit) => {
        return consolidatedFilesize <= filesizeLimit ? '' : `${languageModel.getText('EM00000789', 'Total size of the files should be less than')} ${getFilesizeDisplay(filesizeLimit)}.`;
    }

    const getFilesizeDisplay = (filesizeLimit) => {
        return `${ByteUtils.getDisplayUnit(filesizeLimit) === 'MB' ? ByteUtils.bytesToMebibyte(filesizeLimit) : ByteUtils.bytesToKibibyte(filesizeLimit)}${ByteUtils.getDisplayUnit(filesizeLimit)}`;
    }

    return {
        view: ({ attrs: {
            id = '',
            required = false,
            multiple = false,
            disabled = false,
            accept = '',
            filesizeLimit = ByteUtils.mebibyteToBytes(10), // Default 10MiB
            onTemplateDownloadClick = null,
            requiredInvalidText,
            onInput = DEFAULT_ONINPUT } }) => m(`.${required && !disabled && !uploaded ? '.is-invalid-custom' : ''}`,
                m('.hstack.gap-2.align-items-baseline',
                    m('.flex-grow-1.position-relative',
                        m(`input.form-control${invalidText !== '' ? '.is-invalid' : ''}`, {
                            id,
                            multiple,
                            accept,
                            required,
                            disabled,
                            type: 'file',
                            onchange: handleOnInput(accept.split(','), filesizeLimit, onInput, multiple, required),
                            oninvalid: () => {
                                invalidText = requiredInvalidText;
                            },
                        }),
                        m('small.position-absolute.top-100.end-0', `Maximum file size: ${getFilesizeDisplay(filesizeLimit)}`),
                    ),
                    onTemplateDownloadClick && m('button.btn.btn-primary',
                        {
                            type: 'button',
                            disabled,
                            style: { 'white-space': 'nowrap' },
                            onclick: onTemplateDownloadClick
                        },
                        m('i.bi.bi-file-earmark-arrow-down-fill'), languageModel.getText('LT00000218', 'Download Template')
                    )
                ),
                invalidText && m('.invalid-tooltip', { style: { display: 'inline-block', position: 'relative' } }, invalidText)
            )
    }
}